import React, { useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';

const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

function buildTheme(direction) {
  return createTheme({
    direction,
    palette: {
      primary: { main: '#2C3E73', contrastText: '#FFFFFF' },
      secondary: { main: '#D4AF37' },
      error: { main: '#800020' },
      background: { default: '#FFFFFF', paper: '#FFFFFF' },
      text: { primary: '#2C3E73', secondary: '#555555' },
    },
    typography: {
      fontFamily: ['Lato', 'Roboto', 'sans-serif'].join(','),
      h1: { fontFamily: 'Playfair Display, serif' },
      h2: { fontFamily: 'Playfair Display, serif' },
      h3: { fontFamily: 'Playfair Display, serif' },
      h4: { fontFamily: 'Playfair Display, serif' },
      h5: { fontFamily: 'Playfair Display, serif' },
    },
  });
}

export default function AppTheme({ children }) {
  const { i18n } = useTranslation();
  const isRtl = i18n.language?.startsWith('ar');
  const direction = isRtl ? 'rtl' : 'ltr';

  const theme = useMemo(() => buildTheme(direction), [direction]);

  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', direction);
      document.documentElement.setAttribute('lang', i18n.language || 'en');
    }
  }, [direction, i18n.language]);

  return (
    <CacheProvider value={isRtl ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CacheProvider>
  );
}
